'use client';

import classNames from 'classnames';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FilterItem } from '@/components/organisms/filter-card-list/filter-card-list';
import {
  DateData,
  DurationData,
  DurationOptions,
  LocationChangeParameters,
  LocationData,
  LocationGroup,
  PersonsData,
  Room,
  RoomsState,
  SearchBarDefaultValues,
  SearchBarWrapperSectionData,
  SearchBarWrapperSectionType,
} from '@/components/organisms/search-bar/search-bar.types';
import {
  extractAdultCount,
  extractAdultsPerRoom,
  extractAutomaticRoomAssignment,
  extractCheckedLocations,
  extractChildrenAges,
  extractChildrenAgesPerRoom,
  extractDepartureDate,
  extractDepartureDateAsISO,
  extractDestinationRegionIds,
  extractDurationPrimary,
  extractHotelIds,
  extractTransportationMethod,
  generatePersonsSectionValueString,
  isPersonsSectionValid,
  mergeObjects,
} from '@/components/organisms/search-bar/shared/search-utils/search-utils';
import { AdaptDestinationHierarchy } from '@/integrations/content/content-destinations/destinations-hierarchy/destination-hierarchy.adapter';
import useQueryDestinationHierarchy from '@/integrations/content/content-destinations/destinations-hierarchy/destination-hierarchy.hooks';
import { useQueryHotelsWithOffers } from '@/integrations/offers/hotels-with-offers/hotels-with-offers.hooks';
import { AdaptSearchAvailableDates } from '@/integrations/offers/search/available-dates/available-dates.adapter';
import useQuerySearchAvailableDates from '@/integrations/offers/search/available-dates/available-dates.hooks';
import { trackSubmitSearchEvent } from '@/integrations/tracking/google-tag-manager/scripts';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { useRecentSearchesContext } from '@/libraries/contexts/recent-searches-context/recent-searches-context';
import { useSearchContext } from '@/libraries/contexts/search-context';
import {
  searchPageEvents$,
  SearchPageEventType,
} from '@/libraries/events/page-events';
import { updateUrlWithParameters } from '@/libraries/formatters/update-url-with-parameters';
import useDateFormatter from '@/libraries/formatters/use-date-formatter/use-date-formatter';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';
import { getSearchParameters } from '@/libraries/getters/get-search-parameters';
import useUiEventOutsideHandler from '@/libraries/hooks/use-ui-event-outside';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import validateSameDate from '@/libraries/validators/validate-same-date';
import SearchBarWrapper from './search-bar-wrapper/search-bar-wrapper';
// eslint-disable-next-line css-modules/no-unused-class
import styles from './search-bar.module.scss';

export interface SearchBarProps {
  /** Indicates whether to automatically trigger search when no URL parameters are present */
  autoSearch?: boolean;
  /** Indicates whether the search bar is only buttons to open the widget modal  */
  buttonMode?: boolean;
  /** The class name to apply */
  className?: string;
  /** Indicates whether to show the compact version */
  compact?: boolean;
  /** Indicates whether the search bar is used in the search widget */
  isWidget?: boolean;
  /** Default values for the search bar */
  propDefaultValues?: SearchBarDefaultValues;
  /** The search settings */
  searchSettings: Content.Components['SearchSettingsDto'];
  /** Indicates weather the search should trigger a navigation to the search page (used when the search bar is used on a different page)*/
  shouldNavigate?: boolean;
  /** Indicates weather the search should trigger a refresh of the page with the updated url params */
  shouldRefresh?: boolean;
  /** The travel portal search settings */
  travelPortalSearchSettings?:
    | Content.Components['TravelPortalSearchSettingsDto']
    | undefined;
}

export const SearchBar = forwardRef(
  (
    {
      autoSearch = false,
      buttonMode = false,
      className,
      isWidget = false,
      propDefaultValues,
      searchSettings,
      shouldNavigate = false,
      shouldRefresh = false,
    }: SearchBarProps,
    ref,
  ) => {
    const { t } = useI18n();
    const { dateFormatter } = useDateFormatter();
    const rootRef = useRef<HTMLDivElement>(null);
    const router = useRouter();
    const [recentSearchLocationText, setRecentSearchLocationText] = useState<
      string[] | string
    >();
    const { addRecentSearch } = useRecentSearchesContext();
    const {
      activeSection,
      brand,
      setActiveSection,
      setAdultsNo,
      setAdultsNoPerRoom,
      setAutomaticRoomAssignment,
      setChildrenAges,
      setChildrenAgesPerRoom,
      setDepartureDate,
      setDepartureIds,
      setDestinationIds,
      setDestinationRegionIds,
      setHotelIds,
      setIsClickedEditSearch,
      setPeriodType,
      setTriggeredBy,
      updateCookies,
      ...searchContext
    } = useSearchContext();
    const { configurationFlowAnchorLinkId, hotelDetails } =
      usePageDetailsContext();
    const isLaptop = useWindowSize('laptop');
    const searchParams = useSearchParams();
    const locale = getLocale();

    const hasDefaultValue = (parameterName: string): boolean => {
      // Check if parameter is in query
      const isInQuery = searchParams!.has(parameterName);

      // Check if parameter is in searchDefaults
      const isInDefaults = propDefaultValues
        ? Object.hasOwnProperty.call(propDefaultValues, parameterName)
        : false;

      return isInQuery || isInDefaults;
    };

    const [durationSection, setDurationSection] =
      useState<SearchBarWrapperSectionData>({
        dropdownData: {
          durationOptions: [],
        },
        id: 0,
        isLoading: hasDefaultValue('durationType'),
        // TODO: replace with data from search settings when available
        label: t('items.search.searchBar.duration.durationTitle'),
        placeholder: t('items.search.searchBar.duration.durationPlaceholder'),
        type: SearchBarWrapperSectionType.DURATION,
        value: '',
      });

    const [locationSection, setLocationSection] =
      useState<SearchBarWrapperSectionData>({
        dropdownData: {
          locations: [],
          transportationOptions: [],
        },
        id: 1,
        isLoading:
          hasDefaultValue('destinationIds') || hasDefaultValue('departureIds'),
        // TODO: replace with data from search settings when available
        label: t('items.search.searchBar.location.locationTitle'),
        placeholder: t('items.search.searchBar.location.locationPlaceholder'),
        type: SearchBarWrapperSectionType.LOCATIONS,
      });

    const [personsSection, setPersonsSection] =
      useState<SearchBarWrapperSectionData>({
        dropdownData: {
          adultThreshold: searchSettings.adultAgeThreshold,
          defaultAdultNumber: searchSettings.defaultNumberOfAdultsPerRoom,
          maxPersons: searchSettings.maxNumberOfAdults,
          maxRooms: searchSettings.maxNumberOfRooms,
          roomDistributionWarning: searchSettings.roomDistributionWarning,
          roomsState: {
            automaticRoomAssignment: searchSettings.automaticRoomAssignment,
            rooms: [
              {
                childrenAges: [],
                numberOfAdults: searchSettings.defaultNumberOfAdultsPerRoom,
                numberOfChildren: 0,
              },
            ],
          },
        },
        id: 2,
        isLoading:
          hasDefaultValue('adultsNumber') ||
          hasDefaultValue('childrenAges') ||
          hasDefaultValue('adultsPerRoom') ||
          hasDefaultValue('childrenAgesPerRoom'),
        // TODO: replace with data from search settings when available
        label: t('items.search.searchBar.persons.personsTitle'),
        placeholder: t('items.search.searchBar.persons.personsPlaceholder'),
        type: SearchBarWrapperSectionType.PERSONS,
        value: generatePersonsSectionValueString({
          adults: searchSettings?.selectedPaxDistribution?.adultsNo,
          children: searchSettings?.selectedPaxDistribution?.childrenNo,
          t,
        }),
      });

    const [dateSection, setDateSection] = useState<SearchBarWrapperSectionData>(
      {
        dropdownData: {
          availableDates: [],
        },
        id: 3,
        isLoading: hasDefaultValue('departureDate'),
        // TODO: replace with data from search settings when available
        label: t('items.search.searchBar.date.dateTitle'),
        placeholder: t('items.search.searchBar.date.datePlaceholder'),
        type: SearchBarWrapperSectionType.DATE,
      },
    );

    const generateLocationValue = useCallback(
      ({
        locations,
        transportationOptions,
      }: {
        locations?: LocationGroup[];
        transportationOptions?: FilterItem[];
      }): string[] => {
        const activeTransports =
          transportationOptions
            ?.filter(option => option.isSelected && option.headline)
            .map(option => option.headline) || [];

        const activeLocations =
          locations
            ?.flatMap(group =>
              group.options.filter(
                option => option.checked && !option.disabled && option.label,
              ),
            )
            .map(option => option.label) || [];

        const hasTransports = activeTransports.length > 0;
        const hasLocations = activeLocations.length > 0;

        if (hasTransports) {
          const transportsString = activeTransports.join(', ');

          if (hasLocations) {
            setRecentSearchLocationText([...(activeLocations as string[])]);

            return [
              `${transportsString} ${t('items.common.words.to')}`,
              ...(activeLocations as string[]),
            ];
          }

          setRecentSearchLocationText(
            `${t('items.search.searchBar.location.toAllDestinations')}`,
          );

          return [
            `${transportsString} ${t('items.search.searchBar.location.toAllDestinations')}`,
          ];
        }

        return activeLocations as string[];
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    const getDuration = useCallback(() => {
      return extractDurationPrimary(durationSection);
    }, [durationSection]);

    const getTransportationMethod = useCallback(() => {
      return extractTransportationMethod(
        locationSection,
        isWidget,
        searchContext.departureIds,
      );
    }, [locationSection, isWidget, searchContext.departureIds]);

    const getCheckedLocations = useCallback(() => {
      return extractCheckedLocations(locationSection);
    }, [locationSection]);

    const getHotelIds = useCallback(() => {
      return extractHotelIds({
        hotelDetails,
        isWidget,
        locationSection,
      });
    }, [hotelDetails, isWidget, locationSection]);

    const getDestinationRegionIds = useCallback(() => {
      return extractDestinationRegionIds(locationSection);
    }, [locationSection]);

    const getAdultCount = useCallback(() => {
      return extractAdultCount(personsSection);
    }, [personsSection]);

    const getChildrenAges = useCallback(() => {
      return extractChildrenAges(personsSection);
    }, [personsSection]);

    const getAdultsPerRoom = useCallback(() => {
      return extractAdultsPerRoom(personsSection);
    }, [personsSection]);

    const getChildrenAgesPerRoom = useCallback(() => {
      return extractChildrenAgesPerRoom(personsSection);
    }, [personsSection]);

    const getAutomaticRoomAssignment = useCallback(() => {
      return extractAutomaticRoomAssignment(personsSection);
    }, [personsSection]);

    const getDepartureDate = useCallback(() => {
      return extractDepartureDate(dateSection);
    }, [dateSection]);

    const destinationIdsParam = isWidget ? '' : getCheckedLocations();

    const {
      data: dataDestinationHierarchy,
      isFetching: isFetchingDestinationHierarchy,
    } = useQueryDestinationHierarchy({
      adultsNumber:
        searchSettings.selectedPaxDistribution?.adultsNo.toString() as string,
      durationType: getDuration() as Content.Components['PeriodType'],
      hotelIds: isWidget ? getHotelIds() : '',
    });

    const { data: dataAvailableDates, isFetching: isFetchingAvailableDates } =
      useQuerySearchAvailableDates({
        adultsNumber: getAdultCount()?.toString() as string,
        brand: brand,
        childrenAges: getChildrenAges(),
        departureIds: getTransportationMethod() as string,
        destinationIds: destinationIdsParam,
        destinationRegionIds: getDestinationRegionIds(),
        hotelIds: getHotelIds(),
      });

    const { data: dataSearchSingle } = useQueryHotelsWithOffers({
      batchSize: '1',
      brand: brand,
      departureDate: getDepartureDate() as string,
      departureIds: getTransportationMethod() as string,
      durationType: getDuration() as Content.Components['PeriodType'],
      hotelIds: getHotelIds(),
      ...(getAutomaticRoomAssignment()
        ? {
            adultsNumber: getAdultCount()?.toString() as string,
            childrenAges: getChildrenAges(),
          }
        : {
            adultsPerRoom: getAdultsPerRoom(),
            childrenAgesPerRoom: getChildrenAgesPerRoom(),
          }),
    });

    const setupAndUpdateDurationOptions = () => {
      const newDurationOptions = searchSettings.availablePeriodTypes.map(
        option => {
          let icon: IconName;
          let isSelected: boolean = false;
          let rangeSize: number;

          const urlDefaultValues = getSearchParameters(searchParams!);
          const defaultValues = mergeObjects(
            propDefaultValues,
            urlDefaultValues,
          );

          const selectedDurationType = defaultValues.durationType;

          // TODO: Handle rangeSize issue (#2311) gracefully
          switch (option.value) {
            case 'week':
              icon = 'Calendar';
              rangeSize = 7;
              break;

            case 'miniWeek':
              icon = 'CalendarShortWeek';
              rangeSize = 3;
              break;

            default: {
              icon = 'Plus';
              rangeSize = 0;
            }
          }

          if (option.value === selectedDurationType) {
            isSelected = true;
          }

          return {
            headline: option.label,
            icon,
            id: option.value,
            isSelected,
            rangeSize,
            text: option.description,
          };
        },
      );

      // Fallback
      const anySelected = newDurationOptions.some(option => option.isSelected);

      if (!anySelected && newDurationOptions.length > 0) {
        newDurationOptions[0].isSelected = true;
      }

      setDurationSection(previousSection => {
        return {
          ...previousSection,
          dropdownData: {
            durationOptions: newDurationOptions,
          },
          isLoading: false,
          value: newDurationOptions.find(option => option.isSelected)?.headline,
        };
      });

      setDateSection(previousSection => {
        return {
          ...previousSection,
          dropdownData: {
            ...(previousSection.dropdownData as DateData),
            rangeSize: newDurationOptions.find(option => option.isSelected)
              ?.rangeSize,
          },
        };
      });
    };

    const getDepartureDateParameter = useCallback((): Date | undefined => {
      const urlDefaultValues = getSearchParameters(searchParams!);
      const defaultValues = mergeObjects(propDefaultValues, urlDefaultValues);

      const departureDateParameter = defaultValues.departureDate
        ? new Date(defaultValues.departureDate)
        : undefined;

      return departureDateParameter;
    }, [searchParams, propDefaultValues]);

    useEffect(() => {
      const urlDefaultValues = getSearchParameters(searchParams!);
      const defaultValues = mergeObjects(propDefaultValues, urlDefaultValues);

      const updateDestinationsAndDeparturesInSections = (
        data: AdaptDestinationHierarchy,
      ) => {
        setLocationSection(previousSection => {
          // Departures
          const selectedDepartureId = searchSettings.overrideDeparture
            ? defaultValues?.departureId
            : !isWidget && searchContext.departureIds
              ? searchContext.departureIds
              : defaultValues?.departureId;

          const updatedTransportationOptions = data.departures.map(
            departure => ({
              ...departure,
              isSelected: departure.id === selectedDepartureId,
            }),
          );

          // Destinations
          const selectedLocations = [
            ...(defaultValues?.destinationIds ?? []),
            ...(defaultValues?.destinationRegionIds ?? []),
          ];

          // filter locations based on transportation method
          const updatedLocations = data.destinations?.map(group => ({
            ...group,
            options: group.options.map(location => ({
              ...location,
              checked: selectedLocations.includes(location.id),
              disabled: !location.filterIds?.includes(
                parseInt(selectedDepartureId),
              ),
            })),
          }));

          return {
            ...previousSection,
            dropdownData: {
              ...previousSection.dropdownData,
              locations: updatedLocations,
              transportationOptions: updatedTransportationOptions,
            } as LocationData,
            isLoading: false,
            value: generateLocationValue({
              locations: updatedLocations,
              transportationOptions: updatedTransportationOptions,
            }),
          };
        });
      };

      if (!dataDestinationHierarchy) return;

      updateDestinationsAndDeparturesInSections({
        departures: dataDestinationHierarchy?.departures,
        destinations: dataDestinationHierarchy?.destinations,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataDestinationHierarchy]);

    useEffect(() => {
      const departureDateParameter = getDepartureDateParameter();

      const updateDatesInSections = (data: AdaptSearchAvailableDates) => {
        setDateSection(previousSection => {
          const filteredDates =
            data.result
              ?.filter(item =>
                item.availablePeriodTypes?.includes(
                  getDuration() as Content.Components['PeriodType'],
                ),
              )
              .map(item => new Date(item.departureDate)) ?? [];

          const contextDate =
            !isWidget && searchContext.departureDate
              ? new Date(searchContext.departureDate)
              : undefined;
          const savedDate =
            contextDate ??
            (previousSection.dropdownData as DateData)?.selectedDate;
          const today = new Date();

          today.setUTCHours(0, 0, 0, 0);

          // Check if saved date from cookie is still valid
          const isSavedDateValid =
            savedDate &&
            savedDate >= today &&
            filteredDates.some(date => validateSameDate(date, savedDate));

          const isUrlDateAvailable =
            departureDateParameter &&
            filteredDates.some(date =>
              validateSameDate(date, departureDateParameter),
            );

          // Use saved date if valid, otherwise use URL param if available, otherwise first available date
          const selectedDate = isSavedDateValid
            ? savedDate
            : isUrlDateAvailable
              ? departureDateParameter
              : filteredDates[0];
          const formattedStartDate = selectedDate
            ? dateFormatter(selectedDate).format('D. MMM YYYY')
            : '';

          return {
            ...previousSection,
            dropdownData: {
              ...(previousSection.dropdownData as DateData),
              availableDates: filteredDates,
              selectedDate,
            },
            isLoading: false,
            isNotified: !isSavedDateValid && !isUrlDateAvailable,
            value: formattedStartDate
              ? `${t('items.common.words.from')} ${formattedStartDate}`
              : '',
          };
        });
      };

      if (!dataAvailableDates) return;

      updateDatesInSections(dataAvailableDates);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataAvailableDates, durationSection]);

    const invalidateDateSection = () => {
      setDateSection(previousSection => {
        if (
          previousSection.value === undefined &&
          (previousSection.dropdownData as DateData).selectedDate === undefined
        ) {
          return previousSection;
        }

        return {
          ...previousSection,
          isNotified: true,
        };
      });
    };

    const invalidatePersonsSection = () => {
      setPersonsSection(previousSection => {
        if (isPersonsSectionValid(previousSection)) {
          return {
            ...previousSection,
            isNotified: false,
          };
        }

        return {
          ...previousSection,
          isNotified: true,
        };
      });
    };

    const invalidateLocationSection = useCallback(() => {
      setLocationSection(previousSection => {
        const areLocationsSelected = (
          previousSection.dropdownData as LocationData
        ).locations?.some(group =>
          group.options.some(location => location.checked === true),
        );

        if (areLocationsSelected && !isWidget) {
          return {
            ...previousSection,
            isNotified: true,
            value: undefined,
          };
        }

        return previousSection;
      });
    }, [isWidget]);

    const handleClickOutside = () => {
      if (!isWidget) {
        setActiveSection(-1);
      }
    };

    useUiEventOutsideHandler(rootRef, handleClickOutside);

    const updateLocationSection = useCallback(
      (transportationId: string) => {
        setLocationSection(previousSection => {
          const dropdownData = previousSection.dropdownData as LocationData;
          const selectedTransportationId = transportationId;
          const updatedTransportationOptions =
            dropdownData.transportationOptions.map(option => ({
              ...option,
              isSelected: option.id === selectedTransportationId,
            }));

          // filter locations based on transportation method
          const updatedLocations = dropdownData.locations?.map(group => ({
            ...group,
            options: group.options.map(location => ({
              ...location,
              disabled: !location.filterIds?.includes(
                parseInt(selectedTransportationId),
              ),
            })),
          }));

          return {
            ...previousSection,
            dropdownData: {
              ...(previousSection.dropdownData as LocationData),
              locations: updatedLocations,
              transportationOptions: updatedTransportationOptions,
            },
            value: generateLocationValue({
              locations: updatedLocations,
              transportationOptions: updatedTransportationOptions,
            }),
          };
        });
      },
      [generateLocationValue],
    );

    const handleClickSection = useCallback(
      (sectionId: number) => {
        if (activeSection === sectionId) {
          if (isWidget) return;

          setActiveSection(-1);
        } else {
          setActiveSection(sectionId);

          if (!rootRef.current) return;

          if (!isLaptop) return;

          if (isWidget) return;

          const LARGE_SCROLL_OFFSET = 144;
          const SMALL_SCROLL_OFFSET = 24;
          const elementPosition = rootRef.current.getBoundingClientRect().top;

          if (
            (document.body.getBoundingClientRect().top -
              rootRef.current.getBoundingClientRect().top) *
              -1 <=
            LARGE_SCROLL_OFFSET
          ) {
            window.scrollTo({
              behavior: 'smooth',
              top: elementPosition + window.scrollY - LARGE_SCROLL_OFFSET,
            });
          } else {
            window.scrollTo({
              behavior: 'smooth',
              top: elementPosition + window.scrollY - SMALL_SCROLL_OFFSET,
            });
          }
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [activeSection, isWidget, isLaptop],
    );

    const handleClickClose = useCallback(() => {
      setActiveSection(-1);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClickNext = useCallback(() => {
      setActiveSection(activeSection + 1);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSection]);

    const handleClickBack = useCallback(() => {
      setActiveSection(activeSection - 1);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSection]);

    const handleChangeDuration = useCallback(
      (durationId: string) => {
        let updatedOptions: DurationOptions[];

        setDurationSection(previousSection => {
          updatedOptions = (
            previousSection?.dropdownData as DurationData
          ).durationOptions.map(option => ({
            ...option,
            isSelected: option.id === durationId,
          }));

          return {
            ...previousSection,
            dropdownData: {
              ...previousSection.dropdownData,
              durationOptions: updatedOptions,
            },
            value: updatedOptions.find(option => option.id === durationId)
              ?.headline,
          };
        });

        if (!dataAvailableDates) return;

        const departureDateParameter = getDepartureDateParameter();

        const filteredDates =
          dataAvailableDates.result
            ?.filter(item =>
              item.availablePeriodTypes?.includes(
                durationId as Content.Components['PeriodType'],
              ),
            )
            .map(item => new Date(item.departureDate)) ?? [];

        const isDateAvailable =
          departureDateParameter &&
          filteredDates.some(date =>
            validateSameDate(date, departureDateParameter),
          );

        let selectedDate = departureDateParameter;
        let noValidDates = false;

        if (!isDateAvailable) {
          selectedDate = filteredDates[0];

          if (!selectedDate) {
            noValidDates = true;
          }
        }

        const formattedDate = dateFormatter(selectedDate).format('D. MMM YYYY');
        const valueString = `${t('items.common.words.from')} ${formattedDate}`;

        setDateSection(previousSection => {
          const durationOptions = (
            (previousSection?.dropdownData as DateData)?.durationOptions ?? []
          ).map(option => ({
            ...option,
            isSelected: option.id === durationId,
          }));

          const selectedOption = durationOptions.find(opt => opt.isSelected);
          const baseDropdownData = {
            ...(previousSection?.dropdownData as DateData),
            durationOptions,
            rangeSelect: false,
            rangeSize: selectedOption?.rangeSize,
          };

          return {
            ...previousSection,
            dropdownData: noValidDates
              ? {
                  ...baseDropdownData,
                  availableDates: [],
                  selectedDate: undefined,
                }
              : {
                  ...baseDropdownData,
                  availableDates: filteredDates,
                  selectedDate,
                },
            isNotified: noValidDates ? false : !isDateAvailable,
            value: noValidDates ? '' : valueString,
          };
        });

        invalidateLocationSection();
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [dataAvailableDates],
    );

    const handleChangeTransportation = useCallback(
      (transportationId: string) => {
        updateLocationSection(transportationId);
        invalidateDateSection();
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    const handleChangeLocations = useCallback(
      ({
        destinationRegions,
        destinations,
        hotels,
        locations,
      }: LocationChangeParameters) => {
        let didChange = false;

        const arraysEqual = (a?: any[], b?: any[]) => {
          if (a === b) return true;

          if (!a || !b || a.length !== b.length) return false;

          return a.every((val, index) => val === b[index]);
        };

        setLocationSection(previousSection => {
          const prevDropdownData = previousSection.dropdownData as LocationData;

          const isSame =
            arraysEqual(
              prevDropdownData.destinationRegionIds,
              destinationRegions,
            ) &&
            arraysEqual(prevDropdownData.destinations, destinations) &&
            arraysEqual(prevDropdownData.hotelIds, hotels) &&
            arraysEqual(prevDropdownData.locations, locations);

          didChange = !isSame;

          return {
            ...previousSection,
            dropdownData: {
              ...prevDropdownData,
              destinationRegionIds: destinationRegions,
              destinations,
              hotelIds: hotels,
              locations,
            },
            isNotified: false,
            value: generateLocationValue({
              locations,
              transportationOptions: prevDropdownData.transportationOptions,
            }),
          };
        });

        if (didChange) {
          invalidateDateSection();
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    const handleChangePersons = useCallback((roomsState: RoomsState) => {
      setPersonsSection(previousSection => {
        return {
          ...previousSection,
          dropdownData: {
            ...(previousSection.dropdownData as PersonsData),
            roomsState: roomsState,
          },
          isLoading: false,
          value: generatePersonsSectionValueString({
            roomsState,
            t,
          }),
        };
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChangeDate = useCallback((newDates: Date[]) => {
      let valueString = '';

      const newDate = newDates[0];

      if (newDate !== undefined) {
        const formattedStartDate = dateFormatter(newDate).format('D. MMM YYYY');

        valueString = `${t('items.common.words.from')} ${formattedStartDate}`;
      }

      setDateSection(previousSection => {
        return {
          ...previousSection,
          dropdownData: {
            ...(previousSection.dropdownData as DateData),
            selectedDate: newDate,
          },
          isNotified: false,
          value: valueString,
        };
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateSearchBarStateFromUrl = () => {
      const urlDefaultValues = getSearchParameters(searchParams!);
      const defaultValues = mergeObjects(propDefaultValues, urlDefaultValues);

      const values = {
        adultsNumber:
          (!isWidget && searchContext.adultsNo) || defaultValues.adultsNumber,
        adultsPerRoom:
          (!isWidget && searchContext.adultsNoPerRoom) ||
          defaultValues.adultsPerRoom,
        childrenAges:
          (!isWidget && searchContext.childrenAges) ||
          defaultValues.childrenAges?.join(','),
        childrenAgesPerRoom:
          (!isWidget && searchContext.childrenAgesPerRoom) ||
          defaultValues.childrenAgesPerRoom,
        departureDate:
          (!isWidget && searchContext.departureDate) ||
          defaultValues.departureDate,
        departureId:
          (!isWidget && searchContext.departureIds) ||
          defaultValues.departureId,
      };

      let rooms = [
        {
          childrenAges: [],
          numberOfAdults: searchSettings?.selectedPaxDistribution?.adultsNo,
          numberOfChildren: 0,
        },
      ] as Room[];

      const hasAdultsPerRoom = values.adultsPerRoom;
      const automaticRoomAssignment = !hasAdultsPerRoom;

      if (values.adultsNumber) {
        rooms[0].numberOfAdults = parseInt(values.adultsNumber, 10);
      }

      if (values.childrenAges) {
        rooms[0].childrenAges = values.childrenAges
          .split(',')
          .map(age => parseInt(age, 10));
        rooms[0].numberOfChildren = rooms[0].childrenAges.length;
      }

      if (values.departureId) {
        updateLocationSection(values.departureId);
      }

      if (values.departureDate) {
        handleChangeDate([new Date(values.departureDate)]);
      }

      if (hasAdultsPerRoom) {
        const adultsArray = values.adultsPerRoom
          .split('|')
          .map((number: string) => parseInt(number, 10));

        rooms = adultsArray.map((numberOfAdults: number) => ({
          childrenAges: [],
          numberOfAdults,
          numberOfChildren: 0,
        }));
      }

      if (values.childrenAgesPerRoom) {
        const childrenAgesArray = values.childrenAgesPerRoom
          .split('|')
          .map(room =>
            room
              .split(',')
              .map(age => (isNaN(parseInt(age, 10)) ? null : parseInt(age, 10)))
              .filter(age => age !== null),
          );

        childrenAgesArray.forEach((childrenAges, index) => {
          if (rooms[index]) {
            rooms[index].childrenAges = childrenAges;
            rooms[index].numberOfChildren = childrenAges.length;
          }
        });
      }

      handleChangePersons({ automaticRoomAssignment, rooms });

      if ((urlDefaultValues.hotelIds ?? []).length > 0) {
        setLocationSection(previousSection => {
          return {
            ...previousSection,
            dropdownData: {
              ...(previousSection.dropdownData as LocationData),
              hotelIds: urlDefaultValues.hotelIds,
            },
          };
        });
      }

      if ((urlDefaultValues.destinationRegionIds ?? []).length > 0) {
        setLocationSection(previousSection => {
          return {
            ...previousSection,
            dropdownData: {
              ...(previousSection.dropdownData as LocationData),
              destinationRegionIds: urlDefaultValues.destinationRegionIds,
            },
          };
        });
      }
    };

    const handleClickSearch = useCallback(() => {
      searchPageEvents$.next({ type: SearchPageEventType.SEARCH });

      const constructAndUpdateSearchUrl = () => {
        const adultsNumber = getAdultCount()?.toString() as string;
        const adultsPerRoom = getAdultsPerRoom();
        const childrenAges = getChildrenAges();
        const childrenAgesPerRoom = getChildrenAgesPerRoom();
        const departureDate = getDepartureDate() as string;
        const departureIds = getTransportationMethod() as string;
        const destinationIds = getCheckedLocations();
        const destinationRegionIds = getDestinationRegionIds();
        const durationType = getDuration() as string;
        const hotelIds = getHotelIds();

        const parameters = new URLSearchParams({
          batchSize: searchSettings.batchSize.toString(),
          departureDate,
          departureIds,
          destinationIds,
          destinationRegionIds,
          durationType,
          hotelIds,
          locale: getCulture(locale),
          sortBy: searchSettings.selectedSortBy ?? 'websitePriority',
        });

        if (getAutomaticRoomAssignment()) {
          parameters.append('childrenAges', childrenAges);
          parameters.append('adultsNumber', adultsNumber);
        } else {
          parameters.append('adultsPerRoom', adultsPerRoom);
          parameters.append('childrenAgesPerRoom', childrenAgesPerRoom);
        }

        const parametersObject: Record<string, any> = {};

        parameters.forEach((value, key) => {
          parametersObject[key] = value;
        });

        setTriggeredBy('searchButton');

        updateCookies({
          adultsNumber,
          adultsPerRoom: !getAutomaticRoomAssignment() ? adultsPerRoom : '',
          childrenAges,
          childrenAgesPerRoom: !getAutomaticRoomAssignment()
            ? childrenAgesPerRoom
            : '',
          departureDate: extractDepartureDateAsISO(dateSection),
          departureIds,
          periodType: durationType,
        });

        addRecentSearch({
          locationSection: {
            value: recentSearchLocationText,
          },
          parameters,
        });

        // if searching for a single hotel, navigate directly to that PDP
        if (
          !hotelIds.includes(',') &&
          destinationRegionIds.length === 0 &&
          destinationIds.length === 0 &&
          dataSearchSingle?.redirect
        ) {
          router.push(dataSearchSingle?.redirect);

          return;
        }

        updateUrlWithParameters({
          anchorLinkId: configurationFlowAnchorLinkId,
          newParameters: parametersObject,
          overwrite: true,
          redirectUrl: searchSettings.searchPageUrl,
          router,
          shouldNavigate,
          shouldRefresh,
        });
      };

      invalidatePersonsSection();

      if (
        getDuration() === undefined ||
        getTransportationMethod() === undefined ||
        getDepartureDate() === undefined ||
        !isPersonsSectionValid(personsSection)
      ) {
        return;
      }

      const departureDate = dateFormatter(getDepartureDate()).locale('en');
      const countries = [
        ...new Set(
          getCheckedLocations()
            ?.split(',')
            .map(
              destination =>
                dataDestinationHierarchy?.destinations?.find(available =>
                  available?.options.find(item => item.id === destination),
                )?.groupName,
            ),
        ),
      ];
      const destinations = [
        ...new Set(
          getCheckedLocations()
            ?.split(',')
            .map(
              destination =>
                dataDestinationHierarchy?.destinations
                  ?.filter(available =>
                    available?.options.find(item => item.id === destination),
                  )
                  .map(
                    available =>
                      available?.options.find(item => item.id === destination)
                        ?.label,
                  )?.[0],
            ),
        ),
      ];

      trackSubmitSearchEvent({
        search_details: {
          adults: Number(getAdultCount()),
          children: Number(
            getChildrenAges()?.split(',')[0] !== ''
              ? getChildrenAges()?.split(',').length
              : 0,
          ),
          children_ages: String(getChildrenAges()?.split(',').join(', ')),
          country: String(countries?.join(', ').toLowerCase()),
          departure_date: String(departureDate.format('YYYY-MM-DD')),
          departure_day: String(departureDate.date()),
          departure_month: String(departureDate.format('MMM').toLowerCase()),
          departure_week: String(departureDate.week()),
          departure_week_day: String(
            departureDate.format('dddd').toLowerCase(),
          ),
          departure_year: String(departureDate.year()),
          destination: String(destinations?.join(', ').toLowerCase()),
          duration: getDuration() === 'week' ? 7 : 3,
          duration_type: String(getDuration()),
          travelform: String(
            dataDestinationHierarchy?.departures
              .find(departure => departure.id === getTransportationMethod())
              ?.headline.toLowerCase(),
          ),
        },
      });

      setActiveSection(-1);
      setIsClickedEditSearch(false);
      constructAndUpdateSearchUrl();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      dateSection,
      searchSettings,
      locale,
      shouldNavigate,
      shouldRefresh,
      recentSearchLocationText,
      personsSection,
      dataSearchSingle,
      dataDestinationHierarchy,
      router,
      configurationFlowAnchorLinkId,
      brand,
    ]);

    useEffect(() => {
      setupAndUpdateDurationOptions();
      updateSearchBarStateFromUrl();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const isEnabledSearchButton = useMemo(
      () =>
        getDuration() !== undefined &&
        getTransportationMethod() !== undefined &&
        getDepartureDate() !== undefined,
      [getDuration, getTransportationMethod, getDepartureDate],
    );

    // Trigger default search when entering search page with no URL parameters
    useEffect(() => {
      const urlDefaultValues = getSearchParameters(searchParams!);
      const hasUrlParams =
        urlDefaultValues?.departureDate ||
        urlDefaultValues?.departureId ||
        urlDefaultValues?.destinationIds ||
        urlDefaultValues?.durationType;

      // Only trigger if autoSearch is enabled and no URL params and search bar is ready
      if (
        autoSearch &&
        !hasUrlParams &&
        !isWidget &&
        getDuration() &&
        isEnabledSearchButton
      ) {
        handleClickSearch();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEnabledSearchButton, autoSearch]);

    // update search context on each section change
    useEffect(() => {
      setPeriodType(getDuration() as string);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [durationSection]);

    useEffect(() => {
      setDepartureIds(getTransportationMethod() as string);
      setDestinationIds(getCheckedLocations());
      setHotelIds(getHotelIds());
      setDestinationRegionIds(getDestinationRegionIds());
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationSection]);

    useEffect(() => {
      const childrenAges = getChildrenAges();
      const adultsNumber = getAdultCount()?.toString() as string;
      const adultsPerRoom = getAdultsPerRoom();
      const childrenAgesPerRoom = getChildrenAgesPerRoom();

      if (getAutomaticRoomAssignment()) {
        setAutomaticRoomAssignment(true);
        setChildrenAges(childrenAges);
        setAdultsNo(adultsNumber);
        setAdultsNoPerRoom('');
        setChildrenAgesPerRoom('');
      } else {
        setAutomaticRoomAssignment(false);
        setAdultsNo('');
        setChildrenAges('');
        setAdultsNoPerRoom(adultsPerRoom);
        setChildrenAgesPerRoom(childrenAgesPerRoom);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [personsSection]);

    useEffect(() => {
      setDepartureDate(getDepartureDate() as string);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateSection]);

    useImperativeHandle(ref, () => ({
      search: handleClickSearch,
    }));

    const sections = useMemo(
      () => [durationSection, locationSection, personsSection, dateSection],
      [durationSection, locationSection, personsSection, dateSection],
    );

    return (
      <div
        className={classNames(styles.component, className, {
          [styles.isOpen]: !isWidget && activeSection !== -1,
          [styles.isSearchWidget]: isWidget,
        })}
      >
        <button
          aria-label={t('items.common.words.close')}
          className={styles.overlay}
          onClick={() => {
            setActiveSection(-1);
          }}
          type="button"
        />

        <div ref={rootRef} className={styles.content}>
          <SearchBarWrapper
            activeSection={activeSection}
            buttonLabel={t('items.common.words.search')}
            buttonMode={buttonMode}
            canSubmit={isEnabledSearchButton}
            customRangeStyle
            isLoadingDate={isFetchingAvailableDates}
            isLoadingLocations={isFetchingDestinationHierarchy}
            isWidget={isWidget}
            onChangeDate={handleChangeDate}
            onChangeDuration={handleChangeDuration}
            onChangeLocations={handleChangeLocations}
            onChangePersons={handleChangePersons}
            onChangeTransportation={handleChangeTransportation}
            onClickBack={handleClickBack}
            onClickClose={handleClickClose}
            onClickNext={handleClickNext}
            onClickSearch={handleClickSearch}
            onClickSection={handleClickSection}
            searchSettings={searchSettings}
            sections={sections}
          />
        </div>
      </div>
    );
  },
);

SearchBar.displayName = 'SearchBar';
